'use client'

/**
 * Alerts v3 — Bill prototype 2026-05-30 (.alpill + .algroup + .alrow).
 *
 * Filter pills (All / Needs attention / Completed / Info) → filter the
 * grouped channel sections below. Each alert row: severity-tinted icon
 * tile + title (with red dot when unread) + detail + time-ago + optional
 * action button. Unread alerts keep the red dot beside the title.
 */

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { LobbyHeaderMeta } from '@/contexts/LobbyHeaderMetaContext'
import { DemoDataBadge } from '@/components/DemoDataBadge'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  FilterIcon,
  ArrowUp02Icon,
  ArrowDown02Icon,
  Key01Icon,
  UserGroup03Icon,
  DashboardSpeed01Icon,
  Activity01Icon,
  PlugSocketIcon,
  Shield01Icon,
  CreditCardIcon,
  File01Icon,
  SearchIcon,
  ModernTvIcon,
  HierarchyCircle03Icon,
  Building01Icon,
  Delete02Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import type { IconSvgElement } from '@hugeicons/react'
import {
  AUTOMATION_CHANNEL_META,
  type AlertIconKey,
  type AlertRow,
  type AlertSeverity,
  type AutomationChannel,
} from '@/lib/automations/fallback-data'

const ALERT_ICON: Record<AlertIconKey, IconSvgElement> = {
  filter: FilterIcon,
  'trending-up': ArrowUp02Icon,
  'trending-down': ArrowDown02Icon,
  key: Key01Icon,
  users: UserGroup03Icon,
  gauge: DashboardSpeed01Icon,
  activity: Activity01Icon,
  'plug-off': PlugSocketIcon,
  shield: Shield01Icon,
  wallet: CreditCardIcon,
  file: File01Icon,
}

const CHANNEL_HEADER_ICON: Partial<Record<AutomationChannel, IconSvgElement>> = {
  search: SearchIcon,
  social: UserGroup03Icon,
  tv: ModernTvIcon,
  programmatic: HierarchyCircle03Icon,
  workspace: Building01Icon,
}

const SEVERITY_TINT: Record<AlertSeverity, { bg: string; fg: string }> = {
  crit: { bg: '#FBE3DE', fg: '#C03B25' },
  warn: { bg: '#FBEAC8', fg: '#7A5300' },
  done: { bg: '#C7F5DD', fg: '#0A3D29' },
  info: { bg: '#D9EAFF', fg: '#0A3A5C' },
}

type FilterKey = 'all' | 'attention' | 'done' | 'info'

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'attention', label: 'Needs attention' },
  { key: 'done', label: 'Completed' },
  { key: 'info', label: 'Info' },
]

const CHANNEL_ORDER: AutomationChannel[] = ['search', 'social', 'tv', 'programmatic', 'workspace']
const SNOOZE_OPTIONS = [
  { label: 'Snooze 24 hours', hours: 24 },
  { label: 'Snooze 1 week', hours: 168 },
  { label: 'Snooze 30 days', hours: 720 },
] as const

type DisplayItem =
  | { type: 'alert'; id: string; alert: AlertRow; sortAt: number; severity: AlertSeverity; count: 1 }
  | { type: 'group'; id: string; alerts: AlertRow[]; sortAt: number; severity: AlertSeverity; count: number }

function severityToFilter(s: AlertSeverity): FilterKey {
  if (s === 'crit' || s === 'warn') return 'attention'
  if (s === 'done') return 'done'
  return 'info'
}

function severityRank(severity: AlertSeverity): number {
  if (severity === 'crit') return 3
  if (severity === 'warn') return 2
  if (severity === 'done') return 1
  return 0
}

function maxSeverity(alerts: AlertRow[]): AlertSeverity {
  return alerts.reduce<AlertSeverity>(
    (max, alert) => (severityRank(alert.severity) > severityRank(max) ? alert.severity : max),
    'info',
  )
}

function alertSortTime(alert: AlertRow): number {
  const value = alert.createdAt ? new Date(alert.createdAt).getTime() : 0
  return Number.isFinite(value) ? value : 0
}

function itemTimeBand(sortAt: number): number {
  return Math.floor(sortAt / 3600_000)
}

function compareDisplayItems(a: DisplayItem, b: DisplayItem): number {
  const bandDiff = itemTimeBand(b.sortAt) - itemTimeBand(a.sortAt)
  if (bandDiff !== 0) return bandDiff

  const severityDiff = severityRank(b.severity) - severityRank(a.severity)
  if (severityDiff !== 0) return severityDiff

  return b.sortAt - a.sortAt
}

function buildDisplayItems(alerts: AlertRow[]): DisplayItem[] {
  const groups = new Map<string, AlertRow[]>()
  const ungrouped: AlertRow[] = []

  for (const alert of alerts) {
    const key = alert.kind ?? (alert.eventType ? `legacy:${alert.eventType}` : null)
    if (!key) {
      ungrouped.push(alert)
      continue
    }

    const existing = groups.get(key) ?? []
    existing.push(alert)
    groups.set(key, existing)
  }

  const items: DisplayItem[] = ungrouped.map((alert) => ({
    type: 'alert',
    id: alert.id,
    alert,
    sortAt: alertSortTime(alert),
    severity: alert.severity,
    count: 1,
  }))

  for (const [key, groupAlerts] of groups) {
    const sorted = [...groupAlerts].sort((a, b) => alertSortTime(b) - alertSortTime(a))
    if (sorted.length >= 3) {
      items.push({
        type: 'group',
        id: key,
        alerts: sorted,
        sortAt: alertSortTime(sorted[0]),
        severity: maxSeverity(sorted),
        count: sorted.length,
      })
    } else {
      items.push(...sorted.map((alert) => ({
        type: 'alert' as const,
        id: alert.id,
        alert,
        sortAt: alertSortTime(alert),
        severity: alert.severity,
        count: 1 as const,
      })))
    }
  }

  return items.sort(compareDisplayItems)
}

export function AlertsV3Page({ alerts, isExample = false }: { alerts: AlertRow[]; isExample?: boolean }) {
  const [filter, setFilter] = useState<FilterKey>('all')
  const [rows, setRows] = useState(alerts)
  const [closingId, setClosingId] = useState<string | null>(null)
  const [snoozeMenuId, setSnoozeMenuId] = useState<string | null>(null)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setRows(alerts)
  }, [alerts])

  const closeAlert = async (
    alert: AlertRow,
    body: { action: 'dismiss' } | { action: 'snooze'; durationHours: 24 | 168 | 720 },
  ) => {
    setClosingId(alert.id)
    setSnoozeMenuId(null)
    const previousRows = rows
    setRows((prev) => prev.filter((row) => row.id !== alert.id))

    try {
      const response = await fetch(`/api/alerts/${alert.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!response.ok) throw new Error(`PATCH failed with ${response.status}`)

      if (alert.unread) {
        window.dispatchEvent(new CustomEvent('alerts:deleted', { detail: { unread: true } }))
      }
    } catch (error) {
      console.error('Failed to update alert:', error)
      setRows(previousRows)
      window.alert('Could not update this alert. Try again.')
    } finally {
      setClosingId(null)
    }
  }

  const markRead = (alert: AlertRow) => {
    if (!alert.unread) return

    setRows((prev) => prev.map((row) => (
      row.id === alert.id ? { ...row, unread: false } : row
    )))
    window.dispatchEvent(new CustomEvent('alerts:deleted', { detail: { unread: true } }))
    void fetch(`/api/alerts/${alert.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'read' }),
    }).catch((error) => {
      console.error('Failed to mark alert read:', error)
    })
  }

  const counts = useMemo(() => {
    const c: Record<FilterKey, number> = { all: rows.length, attention: 0, done: 0, info: 0 }
    for (const a of rows) c[severityToFilter(a.severity)] += 1
    return c
  }, [rows])

  const filtered = useMemo(
    () => (filter === 'all' ? rows : rows.filter((a) => severityToFilter(a.severity) === filter)),
    [rows, filter],
  )

  // Group by channel, in the prototype's channel order.
  const grouped = useMemo(() => {
    const orderedChannels = [
      ...CHANNEL_ORDER,
      ...filtered
        .map((alert) => alert.channel)
        .filter((channel) => !CHANNEL_ORDER.includes(channel)),
    ]

    return Array.from(new Set(orderedChannels))
      .map((ch) => ({
        ch,
        items: buildDisplayItems(filtered.filter((a) => a.channel === ch)),
      }))
      .filter((g) => g.items.length > 0)
  }, [filtered])

  const attentionTotal = counts.attention

  const renderAlert = (a: AlertRow) => {
    const tint = SEVERITY_TINT[a.severity]
    const primaryLabel = a.primaryActionLabel ?? a.action

    return (
      <div
        key={a.id}
        className={`bv3-alrow ${a.unread ? 'unread' : ''}`}
      >
        <div
          className="bv3-al-ic"
          style={{ background: tint.bg, color: tint.fg }}
        >
          <HugeiconsIcon icon={ALERT_ICON[a.iconKey]} className="size-4" />
        </div>
        <div className="bv3-al-body">
          <div className="bv3-al-t">
            {a.unread && <span className="ud" />}
            {a.title}
          </div>
          <div className="bv3-al-d">{a.detail}</div>
        </div>
        <div className="bv3-al-meta">
          <span className="bv3-al-time">{a.time}</span>
          {a.secondaryActionHref && a.secondaryActionLabel && (
            <Link
              href={a.secondaryActionHref}
              className="bv3-al-act secondary"
              onClick={() => markRead(a)}
            >
              {a.secondaryActionLabel}
            </Link>
          )}
          {a.primaryActionHref && primaryLabel && (
            <Link
              href={a.primaryActionHref}
              className="bv3-al-act"
              onClick={() => markRead(a)}
            >
              {primaryLabel}
            </Link>
          )}
          <div className="bv3-al-snooze-wrap">
            <button
              type="button"
              className="bv3-al-snooze"
              aria-label={`Snooze alert: ${a.title}`}
              aria-expanded={snoozeMenuId === a.id}
              disabled={closingId === a.id}
              onClick={() => setSnoozeMenuId((current) => (current === a.id ? null : a.id))}
            >
              Snooze
            </button>
            {snoozeMenuId === a.id && (
              <div className="bv3-al-snooze-menu">
                {SNOOZE_OPTIONS.map((option) => (
                  <button
                    key={option.hours}
                    type="button"
                    onClick={() => void closeAlert(a, {
                      action: 'snooze',
                      durationHours: option.hours,
                    })}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            className="bv3-al-delete"
            aria-label={`Dismiss alert: ${a.title}`}
            disabled={closingId === a.id}
            onClick={() => void closeAlert(a, { action: 'dismiss' })}
          >
            <HugeiconsIcon icon={Delete02Icon} className="size-3.5" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bv3-canvas min-h-full">
      <LobbyHeaderMeta>
        {attentionTotal} need attention · {rows.length} total
      </LobbyHeaderMeta>

      <div className="bv3-au pt-6">
        <div className="bv3-au-head">
          <div className="flex flex-wrap items-center gap-2">
            <h1>Alerts</h1>
            {isExample && <DemoDataBadge />}
          </div>
          <div className="bv3-alfilters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`bv3-alpill ${filter === f.key ? 'on' : ''}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
                <span className="n">{counts[f.key]}</span>
              </button>
            ))}
          </div>
        </div>

        {grouped.length === 0 ? (
          <div
            style={{
              color: '#A8A69C', fontSize: 13,
              fontFamily: 'var(--font-mono)',
              padding: '28px 4px',
            }}
          >
            No alerts match this filter.
          </div>
        ) : (
          grouped.map((g) => {
            const meta = AUTOMATION_CHANNEL_META[g.ch]
            const headerIcon = CHANNEL_HEADER_ICON[g.ch]
            const alertCount = g.items.reduce((sum, item) => sum + item.count, 0)
            return (
              <div key={g.ch} className="bv3-algroup">
                <div className="bv3-alg-h">
                  <div className="gt" style={{ background: meta.gradient }}>
                    {headerIcon && <HugeiconsIcon icon={headerIcon} className="size-3.5" />}
                  </div>
                  <span className="gn">{meta.label}</span>
                  <span className="gc">{alertCount}</span>
                </div>
                {g.items.map((item) => {
                  if (item.type === 'alert') return renderAlert(item.alert)

                  const expanded = expandedGroups[item.id] === true
                  const latest = item.alerts[0]
                  const tint = SEVERITY_TINT[item.severity]
                  return (
                    <div key={item.id} className="bv3-alcluster">
                      <button
                        type="button"
                        className={`bv3-alrow bv3-alrow-group ${latest.unread ? 'unread' : ''}`}
                        onClick={() => setExpandedGroups((prev) => ({
                          ...prev,
                          [item.id]: !expanded,
                        }))}
                        aria-expanded={expanded}
                      >
                        <div
                          className="bv3-al-ic"
                          style={{ background: tint.bg, color: tint.fg }}
                        >
                          <HugeiconsIcon icon={ALERT_ICON[latest.iconKey]} className="size-4" />
                        </div>
                        <div className="bv3-al-body">
                          <div className="bv3-al-t">
                            {latest.unread && <span className="ud" />}
                            {latest.title}
                          </div>
                          <div className="bv3-al-d">{item.count} similar alerts</div>
                        </div>
                        <div className="bv3-al-meta">
                          <span className="bv3-al-time">{latest.time}</span>
                          <span className="bv3-al-expand">{expanded ? 'Collapse' : 'Expand'}</span>
                        </div>
                      </button>
                      {expanded && (
                        <div className="bv3-almembers">
                          {item.alerts.map((alert) => renderAlert(alert))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
